'use client';

/**
 * CatalogHero — "Trova la tua barca": hero section con background image
 * dalla galleria admin (riuso settings.hero-background).
 * Picks ultima immagine in lista per differenziare da PremiumHero.
 * Overlay dark per leggibilità testo + SearchSection sopra.
 */

import React, { useEffect, useState } from 'react';

interface Props {
  children: React.ReactNode;
  transparent?: boolean;
  topCue?: React.ReactNode;
  bottomCue?: React.ReactNode;
  /** id DOM della section. Default 'ricerca'; passare null per non emetterlo
   *  (es. dentro PremiumDeck che fornisce già l'id sulla section wrapper). */
  id?: string | null;
}

const CatalogHero: React.FC<Props> = ({ children, transparent = false, topCue, bottomCue, id = 'ricerca' }) => {
  const [bgUrl, setBgUrl] = useState<string | null>(null);

  useEffect(() => {
    if (transparent) return;
    fetch('/api/settings/public/hero-background', { cache: 'no-store' })
      .then(r => r.ok ? r.json() : null)
      .then(data => {
        const urls: string[] = data?.data?.urls ?? [];
        if (urls.length > 0) {
          // Last image per evitare collisione con PremiumHero slideshow start
          setBgUrl(urls[urls.length - 1]);
        }
      })
      .catch(() => {});
  }, [transparent]);

  return (
    <section
      id={id ?? undefined}
      data-cid="catalog-hero"
      className={`snap-start relative px-6 md:px-12 min-h-[100svh] flex items-center overflow-hidden ${transparent ? 'bg-transparent' : 'bg-slate-900'} ${topCue ? 'pt-36' : 'pt-20'} ${bottomCue ? 'pb-32' : 'pb-12'}`}
    >
      {/* Background image — solo se non transparent */}
      {!transparent && bgUrl && (
        <div
          className="absolute inset-0 z-0"
          style={{
            backgroundImage: `url("${bgUrl}")`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            backgroundRepeat: 'no-repeat',
          }}
        />
      )}

      {/* Gradient overlay: scuro forte se opaco, leggero se transparent (per leggibilità contro hero fisso) */}
      <div className={`absolute inset-0 z-[1] ${transparent ? 'bg-gradient-to-b from-slate-950/45 via-slate-950/35 to-slate-950/55' : 'bg-gradient-to-b from-slate-950/70 via-slate-900/55 to-slate-950/80'}`} />

      {/* Content */}
      <div className="relative z-10 max-w-6xl mx-auto w-full">
        <div className="text-center mb-8">
          <span className="inline-block text-[11px] font-bold uppercase tracking-[0.2em] text-sky-200/80 mb-3">
            Catalogo barche
          </span>
          <h2 className="font-display text-3xl md:text-5xl font-black text-white leading-tight tracking-tight drop-shadow-2xl">
            Trova la <em className="font-serif italic font-medium text-sky-300">tua</em> barca
          </h2>
        </div>
        <div data-cid="catalog-hero-search">
          {children}
        </div>
      </div>
      {/* Scroll cues agli estremi della section */}
      {topCue && (
        <div className="absolute top-20 left-1/2 -translate-x-1/2 z-20">
          {topCue}
        </div>
      )}
      {bottomCue && (
        <div className="absolute bottom-6 left-1/2 -translate-x-1/2 z-20">
          {bottomCue}
        </div>
      )}
    </section>
  );
};

export default CatalogHero;
